<template>
  <div class="campaign-preview-container">
    <div class="mobile-frame">
      <!-- Status Bar -->
      <div class="status-bar">
        <div class="status-left">
          <span class="status-time">9:41</span>
        </div>
        <div class="status-right">
          <div class="status-icon signal"></div>
          <div class="status-icon wifi"></div>
          <div class="status-icon battery"></div>
        </div>
      </div>

      <div class="campaign-card" :style="cardStyle">
        <!-- User Header -->
        <div class="user-header">
          <div class="user-info">
            <div class="user-avatar"></div>
            <span class="user-name">Michael C</span>
          </div>
          <div class="user-stats">
            <div class="stat-item">
              <div class="coin-icon-small"></div>
              <span class="stat-value">35</span>
            </div>
            <div class="stat-item">
              <div class="heart-icon-small"></div>
              <span class="stat-value">2</span>
            </div>
          </div>
        </div>

        <!-- Header with icon -->
        <div class="campaign-header">
          <div class="icon-container">
            <img
              v-if="form.iconKey"
              :src="getIconPath(form.iconKey)"
              class="campaign-icon-simple"
              alt="icon"
            />
          </div>
        </div>

        <!-- Challenge Type Badge -->
        <div class="challenge-type-badge">
          <span class="challenge-type" :style="titleStyle">{{ getText(form.campaignCategory, 'CHALLENGE') }}</span>
          <span class="challenge-day" :style="titleStyle">Day {{ getCurrentDay() }} • Streak 🔥 {{ getStreakDays() }}</span>
        </div>

        <!-- Type-specific content -->
        <!-- Gradient background for Quiz -->
        <div v-if="form.inputType === 'quiz'" class="campaign-content-gradient">
          <div class="quiz-preview multiple-choice">
            <div class="question-text" :style="titleStyle">{{ getFirstQuizQuestion() }}</div>
            <div class="quiz-options">
              <div
                v-for="(option, index) in getFirstQuizOptions()"
                :key="index"
                class="quiz-option"
                :style="optionStyle"
              >
                {{ option }}
              </div>
            </div>
            <div class="quiz-hint">Select the right answer</div>
          </div>
        </div>

        <!-- Count Preview with title -->
        <div v-else-if="form.inputType === 'count'" class="campaign-content-with-title">
          <div class="content-title" :style="titleStyle">{{ getText(form.title, "Today's activity count") }}</div>
          <div class="campaign-content">
            <div class="count-preview">
              <div class="counter-value" :style="{ color: form.theme.accentHex || '#7D5ABA' }">2</div>
              <div class="counter-unit" :style="textStyle">{{ getDisplayUnit() }}</div>
              <div class="counter-controls">
                <div class="counter-btn minus" :style="{ boxShadow: `0 3px 0 ${form.theme.accentHex || '#7D5ABA'}` }">−</div>
                <div class="counter-btn plus" :style="{ boxShadow: `0 3px 0 ${form.theme.accentHex || '#7D5ABA'}` }">+</div>
              </div>
              <div v-if="form.goals.dailyEnabled && form.goals.dailyTarget" class="counter-goal" :style="textStyle">
                Daily Goal: {{ form.goals.dailyTarget }} {{ getDisplayUnit() }}
              </div>
            </div>
          </div>
          <div class="counter-encouragement" :style="{ color: form.theme.accentHex || '#7D5ABA' }">Keep going! You're doing great!</div>
          <div class="submit-button" :style="buttonStyle">Submit Count</div>
        </div>

        <!-- Auto Sync Preview with title -->
        <div v-else-if="isAutoSync" class="campaign-content-with-title">
          <div class="content-title" :style="titleStyle">{{ getSyncTitle() }}</div>
          <div class="campaign-content">
            <div class="auto-sync-preview">
              <div class="sync-content">
                <div class="sync-value" :style="textStyle">{{ getSyncValue() }}</div>
                <div class="sync-subtitle" :style="textStyle">{{ getDisplayUnit() }}</div>
                <div class="progress-bar-container">
                  <div class="progress-bar" :class="{ 'inverse-progress': isInverseGoal() }">
                    <div class="progress-fill" :style="[getProgressStyle(), progressStyle]"></div>
                  </div>
                  <div class="sync-goal" :style="textStyle">
                    Goal: {{ form.goals.dailyTarget || '10,000' }} {{ getDisplayUnit() }}
                  </div>
                </div>
                <div class="sync-status">
                  <span class="sync-icon-small">🔄</span>
                  Last sync: 2m ago
                  <span class="manual-edit">(Manually Edit)</span>
                </div>
              </div>
              
            </div>
            
          </div>
          <div class="continue-button" :style="buttonStyle">Continue</div>
        </div>

        <!-- Gradient background for Multiple Selection -->
        <div v-else-if="form.inputType === 'multiple_selection'" class="campaign-content-gradient">
          <div class="checklist-preview">
            <div class="checklist-content">
              <div class="checklist-title" :style="titleStyle">{{ getText(form.title, "Complete your morning routine") }}</div>
              <div class="checklist-items">
                <div
                  v-for="(item, index) in getChecklistItems()"
                  :key="index"
                  class="checklist-item"
                >
                  <div class="checkbox" :class="{ 'checked': index === 0 }" :style="checkboxStyle">{{ index === 0 ? '✓' : '' }}</div>
                  <span :style="textStyle">{{ item }}</span>
                </div>
              </div>
            </div>
            <div class="done-button" :style="buttonStyle">I'm done for now</div>
          </div>
        </div>

        <div v-else-if="form.inputType === 'yes_no'" class="campaign-content-gradient">
          <div class="yesno-preview">
            <div class="yesno-question" :style="titleStyle">{{ getText(form.title, "Did you complete today's goal?") }}</div>
            <div class="yesno-buttons">
              <div class="yesno-button yes-button" :style="buttonStyle">{{ getYesNoOptionText('yes') }}</div>
              <div class="yesno-button no-button" :style="buttonStyle">{{ getYesNoOptionText('no') }}</div>
            </div>
          </div>
        </div>

        <!-- Footer with charging status -->
        <div class="campaign-footer">
          <div class="charging-text">
            ⚡ Charging (+{{ form.reward.coins || form.reward.chargedCoins || 6 }} coins)
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CampaignPreview",
  props: {
    form: {
      type: Object,
      required: true,
    },
    language: {
      type: String,
      default: 'en',
    },
  },
  computed: {
    isAutoSync() {
      return this.form.inputType && this.form.inputType.startsWith('auto_sync');
    },
    cardStyle() {
      const gradients = {
        purple: "linear-gradient(210deg, #FAF5FF 0%, #B097D8 100%)",
        yellow: "linear-gradient(210deg, #FFFCDD 0%, #FDE456 100%)",
        pink: "linear-gradient(210deg, #FFFAFF 0%, #FF9AE3 100%)",
        green: "linear-gradient(210deg, #DDFFF6 0%, #68BCAC 100%)",
        orange: "linear-gradient(210deg, #FFE9DD 0%, #FDB056 100%)",
      };
      return {
        background: gradients[this.form.theme.gradientKey] || gradients.purple,
      };
    },
    badgeStyle() {
      return {
        backgroundColor: this.form.theme.accentHex || "#7D5ABA",
      };
    },
    optionStyle() {
      return {
        borderColor: this.form.theme.accentHex || "#7D5ABA",
        boxShadow: `0 3px 0 ${this.form.theme.accentHex || "#7D5ABA"}`,
      };
    },
    progressStyle() {
      return {
        backgroundColor: this.form.theme.accentHex || "#7D5ABA",
      };
    },
    checkboxStyle() {
      return {
        borderColor: this.form.theme.accentHex || "#7D5ABA",
      };
    },
    textStyle() {
      return {
        color: this.form.theme.accentHex || "#7D5ABA",
      };
    },
    buttonStyle() {
      return {
        color: this.form.theme.accentHex || "#7D5ABA",
        boxShadow: `0 3px 0 ${this.form.theme.accentHex || "#7D5ABA"}`,
      };
    },
    titleStyle() {
      // Create darker version of accent color for text on gradient backgrounds
      const darkerColors = {
        purple: "#360065",
        yellow: "#6E5518",
        pink: "#6E1861",
        green: "#00654B",
        orange: "#6E4018",
      };
      const gradientKey = this.form.theme.gradientKey || "purple";
      return {
        color: darkerColors[gradientKey] || darkerColors.purple,
      };
    },
  },
  methods: {
    // Helper to get text from multilingual field with fallback
    getText(field, fallback = '') {
      if (!field) return fallback;
      // If it's a string (old format), return directly
      if (typeof field === 'string') return field || fallback;
      // If it's an object (new multilingual format), get by language with fallback
      if (typeof field === 'object') {
        return field[this.language] || field.en || fallback;
      }
      return fallback;
    },
    getIconPath(iconKey) {
      // Check if iconKey is a full URL (new format)
      if (iconKey && iconKey.startsWith('http')) {
        return iconKey;
      }

      // Legacy format: icon key that requires local asset
      try {
        return require(`@/assets/icons/${iconKey}.png`);
      } catch {
        return require("@/assets/icons/ic_star_24px.png");
      }
    },
    getCurrentDay() {
      if (!this.form.timing.startDate) return 3;
      const start = new Date(this.form.timing.startDate);
      const now = new Date();
      const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24));
      return Math.max(1, diff + 1);
    },
    getStreakDays() {
      // For preview, show a sample streak
      const day = this.getCurrentDay();
      return Math.min(day, 7); // Cap at 7 for preview
    },
    getChallengeType() {
      const types = {
        quiz: "PRODUCT KNOWLEDGE",
        count: "ACTIVITY TRACKER",
        auto_sync_steps: "STEPS CHALLENGE",
        auto_sync_sleep: "SLEEP CHALLENGE",
        auto_sync_screen: "SCREEN TIME CHALLENGE",
        auto_sync_exercise: "WELLNESS CHALLENGE",
        auto_sync_distance: "DISTANCE CHALLENGE",
        multiple_selection: "WELLNESS CHALLENGE",
        yes_no: "WELLNESS CHALLENGE",
      };
      return types[this.form.inputType] || "CHALLENGE";
    },
    hasQuizQuestions() {
      return this.form.quizQuestions && this.form.quizQuestions.length > 0;
    },
    getFirstQuizQuestion() {
      if (this.hasQuizQuestions() && this.form.quizQuestions[0].question) {
        return this.getText(this.form.quizQuestions[0].question, "Which benefit does our new wellness program offer?");
      }
      return "Which benefit does our new wellness program offer?";
    },
    getFirstQuizOptions() {
      if (this.hasQuizQuestions() && this.form.quizQuestions[0].options) {
        const options = this.form.quizQuestions[0].options;
        // Handle multilingual format: { en: [...], th: [...] }
        if (typeof options === 'object' && !Array.isArray(options)) {
          const langOptions = options[this.language] || options.en || [];
          return langOptions.filter(opt => opt && opt.trim() !== '');
        }
        // Handle old array format
        if (Array.isArray(options)) {
          return options.filter(opt => opt && opt.trim() !== '');
        }
      }
      return [
        "Mental health support",
        "Fitness reimbursement",
        "Flexible scheduling",
        "All of the above"
      ];
    },
    getRewardText() {
      const reward = this.form.reward;
      if (reward.type === "instant") {
        return `Earn ${reward.coins || 10} coins instantly`;
      } else if (reward.type === "charged") {
        return `Earn ${reward.chargedCoins || 50} coins on completion`;
      } else if (reward.type === "prize_pool") {
        return `Split prize pool at end`;
      }
      return "Reward";
    },
    getSyncIcon() {
      const icons = {
        auto_sync_steps: '👟',
        auto_sync_sleep: '😴',
        auto_sync_screen: '📱',
        auto_sync_exercise: '💪',
        auto_sync_distance: '🏃',
      };
      return icons[this.form.inputType] || '📊';
    },
    getSyncTitle() {
      // Use custom title if provided (handle multilingual format)
      const title = this.getText(this.form.title);
      if (title && title.trim()) {
        return title;
      }
      // Fallback to default titles - using "Yesterday's" for metrics that accumulate throughout the day
      const titles = {
        auto_sync_steps: "Yesterday's step count",
        auto_sync_sleep: "Last night's sleep",
        auto_sync_screen: "Yesterday's screen time",
        auto_sync_exercise: "Yesterday's exercise",
        auto_sync_distance: "Yesterday's distance",
      };
      return titles[this.form.inputType] || "Yesterday's progress";
    },
    getSyncValue() {
      const values = {
        auto_sync_steps: '8,248',
        auto_sync_sleep: '7.5',
        auto_sync_screen: '3.2',
        auto_sync_exercise: '45',
        auto_sync_distance: '5.2',
      };
      return values[this.form.inputType] || '82';
    },
    getSyncUnit() {
      const units = {
        auto_sync_steps: 'STEPS',
        auto_sync_sleep: 'HOURS',
        auto_sync_screen: 'HOURS',
        auto_sync_exercise: 'MINUTES',
        auto_sync_distance: 'KM',
      };
      return units[this.form.inputType] || 'UNITS';
    },
    getDisplayUnit() {
      // For auto-sync types, always use the predefined units
      if (this.isAutoSync) {
        return this.getSyncUnit();
      }
      // Use custom unit from goals if set (handle multilingual format)
      if (this.form.goals && this.form.goals.unit) {
        const unit = this.getText(this.form.goals.unit);
        return unit ? unit.toUpperCase() : 'ITEMS';
      }
      return 'ITEMS';
    },
    isInverseGoal() {
      // These activity types have inverse goals (lower is better)
      const inverseTypes = ['auto_sync_screen'];
      return inverseTypes.includes(this.form.inputType);
    },
    getProgressStyle() {
      if (this.isInverseGoal()) {
        // For screen time, show red if over limit, green if under
        return { width: '32%' }; // Example: 3.2 hours out of 10 hour limit
      }
      return { width: '82%' }; // Normal progress
    },
    getChecklistItems() {
      if (this.form.checklistItems && this.form.checklistItems.length > 0) {
        // Handle multilingual format: each item is { en: "...", th: "..." }
        return this.form.checklistItems
          .map(item => this.getText(item))
          .filter(item => item && item.trim() !== '');
      }
      // Default checklist items for preview
      return [
        "Drink water",
        "10-min meditation",
        "Healthy breakfast",
        "Exercise 20 min"
      ];
    },
    getYesNoOptionText(option) {
      // Get the Yes/No option text based on form settings
      // Handle multilingual format: { yes: { en: "...", th: "..." }, no: { en: "...", th: "..." } }
      if (this.form.yesNoOptions && this.form.yesNoOptions[option]) {
        const optionValue = this.form.yesNoOptions[option];
        // If it's a multilingual object
        if (typeof optionValue === 'object') {
          return optionValue[this.language] || optionValue.en || (option === 'yes' ? 'Yes, I did!' : 'No');
        }
        // If it's a plain string (legacy format)
        return optionValue;
      }
      // Default fallbacks based on yesNoSuccessAnswer
      const isNoCorrect = this.form.yesNoSuccessAnswer === 'no';
      if (option === 'yes') {
        return isNoCorrect ? 'Yes' : 'Yes, I did!';
      } else {
        return isNoCorrect ? "No, I didn't!" : 'No';
      }
    },
  },
};
</script>

<style scoped>
.campaign-preview-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px 0;
  min-height: 100%;
}

.mobile-frame {
  width: 375px;
  background: white;
  border-radius: 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transform: scale(0.95);
  transform-origin: top center;
  position: relative;
  margin: 0 auto;
}

/* Status Bar */
.status-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 44px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  z-index: 100;
  color: white;
  font-size: 15px;
  font-weight: 600;
}

.status-left {
  flex: 1;
}

.status-time {
  letter-spacing: -0.3px;
}

.status-right {
  display: flex;
  gap: 6px;
  align-items: center;
}

.status-icon {
  width: 16px;
  height: 12px;
  background: white;
  opacity: 0.9;
  border-radius: 2px;
}

.status-icon.signal {
  width: 18px;
  -webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 12' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='3' height='12' rx='1'/%3E%3Crect x='5' y='3' width='3' height='9' rx='1'/%3E%3Crect x='10' y='6' width='3' height='6' rx='1'/%3E%3Crect x='15' y='9' width='3' height='3' rx='1'/%3E%3C/svg%3E");
  mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 12' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='3' height='12' rx='1'/%3E%3Crect x='5' y='3' width='3' height='9' rx='1'/%3E%3Crect x='10' y='6' width='3' height='6' rx='1'/%3E%3Crect x='15' y='9' width='3' height='3' rx='1'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  mask-size: contain;
}

.status-icon.wifi {
  width: 15px;
  -webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 15 12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.5 12a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM2.5 6.5c2.8-2.5 6.7-2.5 9.5 0M0 3.5c4.1-3.5 10.4-3.5 14.5 0'/%3E%3C/svg%3E");
  mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 15 12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.5 12a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM2.5 6.5c2.8-2.5 6.7-2.5 9.5 0M0 3.5c4.1-3.5 10.4-3.5 14.5 0'/%3E%3C/svg%3E");
  -webkit-mask-size: contain;
  mask-size: contain;
}

.status-icon.battery {
  width: 24px;
  border-radius: 4px;
}

/* User Header */
.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 44px 0 20px 0;
  margin-bottom: 12px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.5);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 36'%3E%3Ccircle cx='18' cy='14' r='6' fill='%23fff' opacity='0.6'/%3E%3Cpath d='M6 30c0-6.6 5.4-12 12-12s12 5.4 12 12' fill='%23fff' opacity='0.6'/%3E%3C/svg%3E");
  background-size: cover;
}

.user-name {
  font-size: 16px;
  font-weight: 700;
  color: #2D2E36;
}

.user-stats {
  display: flex;
  gap: 14px;
  align-items: center;
  background: rgba(255, 255, 255, 1);
  margin-right: -24px;
  padding-left: 12px;
  padding-right: 12px;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.stat-item {
  display: flex;
  align-items: center;
  /* background: rgba(255, 255, 255, 0.25); */
  padding: 8px 6px;
  border-radius: 20px;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #2D2E36;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.coin-icon-small {
  width: 18px;
  height: 18px;
  background-image: url("~@/assets/images/ic_coin.png");
  background-size: contain;
  background-repeat: no-repeat;
}

.heart-icon-small {
  width: 18px;
  height: 18px;
  background-image: url("~@/assets/images/ic_gem.png");
  background-size: contain;
  background-repeat: no-repeat;
}

.campaign-card {
  padding: 0 24px 28px 24px;
  color: white;
  min-height: 660px;
  display: flex;
  flex-direction: column;
}

.campaign-header {
  text-align: center;
  /* margin-bottom: 16px; */
}

.icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
}

.campaign-icon {
  width: 88px;
  height: 88px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
}

.campaign-icon-simple {
  width: 110px;
  height: 110px;
}

.campaign-title {
  font-size: 24px;
  font-weight: 700;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  line-height: 1.3;
  letter-spacing: -0.4px;
  padding: 0 12px;
}

/* Challenge Type Badge */
.challenge-type-badge {
  text-align: center;
  margin-bottom: 24px;
}

.challenge-type {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  opacity: 0.9;
  margin-bottom: 4px;
  word-break: break-word;
}

.challenge-day {
  display: block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.campaign-content {
  background: white;
  border-radius: 22px;
  padding: 28px 24px;
  margin-bottom: 20px;
  color: #404243;
  min-height: 320px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Gradient background container for Yes/No and Multiple Selection */
.campaign-content-gradient {
  padding: 0 24px 28px 24px;
  margin-bottom: 20px;
  min-height: 320px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Container for Count and Auto Sync with title above white box */
.campaign-content-with-title {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.campaign-content-with-title .campaign-content {
  margin-bottom: 0;
  min-height: auto;
}

.content-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.3;
  letter-spacing: -0.3px;
  word-break: break-word;
}

/* Quiz Preview - Common */
.quiz-preview {
  display: flex;
  flex-direction: column;
  min-height: 280px;
}

/* Quiz Preview - Multiple Choice */
.quiz-preview.multiple-choice {
  justify-content: center;
}

.question-text {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #2D2E36;
  text-align: center;
  line-height: 1.4;
  word-break: break-word;
}

.quiz-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.quiz-option {
  padding: 16px 18px 16px 24px;
  border: 2px solid;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  text-align: left;
  color: #2D2E36;
}

.quiz-hint {
  text-align: center;
  font-size: 13px;
  color: white;
  font-weight: 500;
  margin-top: auto;
}

/* Count Preview */
.count-preview {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  padding: 12px 0;
}

.counter-value {
  font-size: 72px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 4px;
  margin-top: 12px;
  line-height: 1;
}

.counter-unit {
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
  margin-bottom: 20px;
  opacity: 0.8;
}

.counter-goal {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  margin-top: 16px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
}

.counter-controls {
  display: flex;
  justify-content: center;
  gap: 24px;
}

.counter-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 2px solid #D1D5DB;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s;
}

.counter-btn:hover {
  border-color: #9CA3AF;
  background: #F9FAFB;
}

.counter-encouragement {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
  margin-top: 24px;
}

.submit-button {
  padding: 16px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.3px;
  background: white;
  box-shadow: 0 3px 0 rgba(0, 0, 0, 1);
}

/* Auto Sync Preview */
.auto-sync-preview {
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}

.sync-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.sync-value {
  font-size: 56px;
  font-weight: 700;
  color: #6C5341;
  margin-bottom: 6px;
  line-height: 1;
  letter-spacing: -1px;
}

.sync-subtitle {
  font-size: 13px;
  font-weight: 700;
  color: #9CA3AF;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
}

.progress-bar-container {
  margin-bottom: 20px;
}

.progress-bar {
  height: 10px;
  background: #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
  position: relative;
}

.progress-bar.inverse-progress {
  background: linear-gradient(to right, #10B981 0%, #10B981 50%, #EF4444 50%, #EF4444 100%);
  opacity: 0.3;
}

.progress-bar.inverse-progress .progress-fill {
  background: #10B981 !important;
  opacity: 1;
}

.progress-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s;
}

.sync-goal {
  font-size: 13px;
  color: #6B7280;
  font-weight: 600;
}

.sync-status {
  font-size: 12px;
  color: #6B7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.sync-icon-small {
  font-size: 12px;
}

.manual-edit {
  color: #6C30CC;
  font-weight: 600;
  cursor: pointer;
}

.continue-button {
  padding: 16px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.3px;
  margin-top: 24px;
}

/* Checklist Preview */
.checklist-preview {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 280px;
  padding: 8px 0;
}

.checklist-content {
  flex: 1;
}

.checklist-title {
  font-size: 19px;
  font-weight: 700;
  margin-bottom: 24px;
  text-align: center;
  word-break: break-word;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
  color: white;
  font-weight: 500;
}

.checkbox {
  width: 26px;
  height: 26px;
  border: 2px solid;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.2s;
  background: white;
}

.checkbox.checked {
  background: currentColor;
  color: white;
}

.done-button {
  padding: 16px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.3px;
}

/* Yes/No Preview */
.yesno-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.yesno-question {
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
  color: #404243;
  word-break: break-word;
}

.yesno-buttons {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
}

.yesno-button {
  padding: 18px 24px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.2px;
}

.yes-button {
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.no-button {
  background: white;
  border: 2px solid #E5E7EB;
  font-weight: 600;
}

/* Footer */
.campaign-footer {
  text-align: center;
  margin-top: auto;
  padding-top: 16px;
}

.charging-text {
  font-size: 13px;
  font-weight: 600;
  color: #f5ce0b;
  letter-spacing: 0.2px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
</style>

